import Head from 'next/head';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { NextSeo } from 'next-seo';
import Combat from '@cont/Combat/Landing/Landing';
import { fetchTopologyInfo } from '@actions/topologies';
import { useEffect } from 'react';
import { useRouter } from 'next/router';
import {
  fetchContestInfo,
  fetchUpcomingCombatsInfo,
  fetchContestRating,
  fetchMyRating
} from '@actions/combats';
import setupInitialProps from '@utils/setupInitialProps';
import Layout from '@layout/FreeLayout/FreeLayout';
import escape from '@utils/escapeHTML';
import PageReduxProvider from '@cont/App/PageReduxProviderWrapper';

const ContestPage = ({ goalUID, goalSlug }) => {
  const router = useRouter();
  const querySlug = router.query.goalSlug;

  useEffect(() => {
    if (querySlug !== goalSlug)
      router.push(
        '/combat/[goalSlug]/[goalUID]',
        `/combat/${goalSlug}/${goalUID}/`,
        {
          shallow: true
        }
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const {
    title = '',
    description = '',
    ogImage,
    twitterOgImage
  } = useSelector(
    ({ combats }) => combats.staticData?.[goalUID]?.metaData || {}
  );
  const canonicalURL = `https://unacademy.com/combat/${goalSlug}/${goalUID}/`;
  const imageURL =
    ogImage ||
    'https://static.uacdn.net/static/dist/c76c2e18c7980818a913f9467c74c567.png';
  return (
    <>
      <NextSeo
        title={title}
        description={description}
        canonical={canonicalURL}
        openGraph={{
          title,
          description,
          images: [
            {
              url: imageURL,
              width: 800,
              height: 600
            }
          ],
          url: canonicalURL
        }}
        twitter={{
          images: [{ url: twitterOgImage }]
        }}
      />
      <Head>
        <title>{title}</title>
      </Head>
      <Combat />
    </>
  );
};

ContestPage.propTypes = {
  goalUID: PropTypes.string.isRequired,
  goalSlug: PropTypes.string.isRequired
};

ContestPage.pageInfo = {
  name: 'Combat Page',
  isMWebSupported: true,
  layoutOptions: {
    hideHeader: true,
    hideFooter: true,
    hideLearnerMinutes: true,
    disableDarkMode: true,
    hideIntercom: true,
    hideFreshbot: true
  }
};

ContestPage.getInitialProps = setupInitialProps(
  async ({ store, query, isServer, req }) => {
    const { goalUID } = query;
    const promises = [];
    let state = store.getState();
    if (!state.topologies?.data[goalUID]?.goalSlug)
      await store.dispatch(fetchTopologyInfo(goalUID));
    state = store.getState();
    if (!state.combats.upcoming?.[goalUID])
      promises.push(store.dispatch(fetchUpcomingCombatsInfo(goalUID)));
    if (!state.combats.staticData?.[goalUID])
      promises.push(store.dispatch(fetchContestInfo(goalUID)));
    if (!state.combats.contestRating?.[goalUID])
      promises.push(store.dispatch(fetchContestRating(goalUID)));
    if (!state.combats.myRating?.[goalUID])
      promises.push(store.dispatch(fetchMyRating(goalUID)));
    if (promises.length) await Promise.all(promises);
    return {
      goalUID: escape(goalUID),
      goalSlug: state.topologies?.data[goalUID]?.goalSlug,
      isServer,
      userAgent: isServer ? req.headers['user-agent'] : ''
    };
  },
  ContestPage.pageInfo
);

export default ContestPage;
ContestPage.Layout = Layout;
ContestPage.Wrapper = PageReduxProvider;
